// Settings Component - Tenant Management (Admin Only)
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Alert,
  Divider,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Business as BusinessIcon,
  Receipt as ReceiptIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import apiService from '../../services/api';
import authService from '../../services/auth';

const Settings = () => {
  const [tenant, setTenant] = useState({
    name: '',
    email: '',
    phone: '',
    company_name: '',
    gst_number: '',
    website: '',
    currency: 'INR',
    subscription_plan: 'basic',
    status: 'active',
    photo: '',
    // New address fields
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    country: '',
    zip_code: '',
    // Invoice fields
    invoice_name: '',
    invoice_email: '',
    invoice_phone: '',
    invoice_mobile: '',
    // New invoice address fields
    invoice_address_line1: '',
    invoice_address_line2: '',
    invoice_city: '',
    invoice_state: '',
    invoice_country: '',
    invoice_zip_code: '',
  });

  const [states, setStates] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);

  const user = authService.getUser();

  useEffect(() => {
    fetchTenantSettings();
    fetchStates();
  }, []);

  const fetchTenantSettings = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/tenant/settings');
      const settings = response.data.settings;
      setTenant(settings);

      // Keep AuthService tenant cache in sync so Layout can immediately reflect changes (logo, etc.)
      try {
        const existingTenant = authService.getTenant() || {};
        const mergedTenant = { ...existingTenant, ...settings };
        sessionStorage.setItem('tenant', JSON.stringify(mergedTenant));
        localStorage.setItem('tenant', JSON.stringify(mergedTenant));
        window.dispatchEvent(new Event('tenantUpdated'));
      } catch (e) {
        // ignore storage errors
      }
    } catch (error) {
      console.error('Error fetching tenant settings:', error);
      setError('Failed to fetch tenant settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await apiService.get('/states');
      if (response.success) {
        setStates(response.data);
      }
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setTenant(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const updateData = {
        name: tenant.name,
        phone: tenant.phone,
        address_line1: tenant.address_line1,
        address_line2: tenant.address_line2,
        company_name: tenant.company_name,
        gst_number: tenant.gst_number,
        website: tenant.website,    
        city: tenant.city,
        state: tenant.state,
        country: tenant.country,
        zip_code: tenant.zip_code,
        // Invoice fields
        invoice_name: tenant.invoice_name,
        invoice_email: tenant.invoice_email,
        invoice_phone: tenant.invoice_phone,
        invoice_mobile: tenant.invoice_mobile,
        // New invoice address fields
        invoice_address_line1: tenant.invoice_address_line1,
        invoice_address_line2: tenant.invoice_address_line2,
        invoice_city: tenant.invoice_city,
        invoice_state: tenant.invoice_state,
        invoice_country: tenant.invoice_country,
        invoice_zip_code: tenant.invoice_zip_code,
      };

      await apiService.put('/tenant/settings', updateData);
      setSuccess('Settings updated successfully!');
      
      // Refresh the data after saving
      await fetchTenantSettings();
    } catch (error) {
      console.error('Error updating settings:', error);
      setError(error.response?.data?.error || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setLogoFile(file);
  };

  const handleUploadLogo = async () => {
    if (!logoFile) {
      setError('Please choose a logo file first');
      return;
    }

    try {
      setLogoUploading(true);
      setError('');
      setSuccess('');

      const formData = new FormData();
      formData.append('logo', logoFile);

      const response = await apiService.postFormData('/tenant/logo', formData);
      if (response?.success) {
        setSuccess('Logo uploaded successfully!');
        setLogoFile(null);
        // Refresh tenant settings so UI picks up the saved filename from DB (photo/logo column)
        await fetchTenantSettings();
      } else {
        setError(response?.message || response?.error || 'Failed to upload logo');
      }
    } catch (err) {
      console.error('Error uploading logo:', err);
      setError(err?.responseData?.message || err?.message || 'Failed to upload logo');
    } finally {
      setLogoUploading(false);
    }
  };

  // Check if user is Admin
  const isAdmin = user?.role_name === 'Admin' || user?.role_name === 'Super Admin';

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access denied. Only administrators can access settings.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading settings...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SettingsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Settings
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchTenantSettings}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Business Information */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <BusinessIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Business Information
                </Typography>
              </Box>

              {/* Logo Upload */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Logo
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button variant="outlined" component="label">
                    Choose Logo
                    <input
                      type="file"
                      hidden
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      onChange={handleLogoFileChange}
                    />
                  </Button>

                  <Typography variant="body2" color="text.secondary">
                    {logoFile ? logoFile.name : (tenant.photo ? `Current: ${tenant.photo}` : 'No logo uploaded')}
                  </Typography>

                  <Button
                    variant="contained"
                    onClick={handleUploadLogo}
                    disabled={!logoFile || logoUploading}
                    sx={{ textTransform: 'none', fontWeight: 'bold' }}
                  >
                    {logoUploading ? 'Uploading...' : 'Upload'}
                  </Button>
                </Box> 
                {(tenant.photo || tenant.logo) && (
                  <Box sx={{ mt: 2 }}>
                    <img
                      src={`${apiService.baseURL}/public/tenant-logo/${encodeURIComponent(tenant.photo || tenant.logo)}`}
                      alt="Tenant Logo"
                      style={{ maxHeight: 64, maxWidth: 240, objectFit: 'contain' }}
                      onError={(e) => {
                        // Hide broken image if file is missing.
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <div> 
                Business ID : </div> 
                 <div>{tenant.id } </div>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Business Name"
                  value={tenant.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                />
                <TextField
                  fullWidth
                  label="Email"
                  value={tenant.email || ''}
                  disabled
                  helperText="Email cannot be changed"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography variant="body2" color="text.secondary">
                          📧
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={tenant.phone || ''}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography variant="body2" color="text.secondary">
                          📞
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="Currency"
                  value={tenant.currency || 'INR'}
                  disabled
                  helperText="Currency is set by subscription"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography variant="body2" color="text.secondary">
                          💰
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <TextField
                  fullWidth
                  label="Address Line 1"
                  value={tenant.address_line1 || ''}
                  onChange={(e) => handleInputChange('address_line1', e.target.value)}
                  placeholder="Street address, building, etc."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography variant="body2" color="text.secondary">
                          📍
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="Address Line 2"
                  value={tenant.address_line2 || ''}
                  onChange={(e) => handleInputChange('address_line2', e.target.value)}
                  placeholder="Apartment, suite, unit, etc. (optional)"
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <TextField
                  fullWidth
                  label="City"
                  value={tenant.city || ''}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  placeholder="City"
                />
                <FormControl fullWidth>
                  <InputLabel>State/Province</InputLabel>
                  <Select
                    value={tenant.state || ''}
                    label="State/Province"
                    onChange={(e) => handleInputChange('state', e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Select State</em>
                    </MenuItem>
                    {states.map((state) => (
                      <MenuItem key={state.id} value={state.id}>
                        {state.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <TextField
                  fullWidth
                  label="Country"
                  value={tenant.country || ''}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  placeholder="Country"
                />
                <TextField
                  fullWidth
                  label="ZIP/Postal Code"
                  value={tenant.zip_code || ''}
                  onChange={(e) => handleInputChange('zip_code', e.target.value)}
                  placeholder="ZIP or Postal Code"
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <TextField
                  fullWidth
                  label="Status"
                  value={tenant.status || 'active'}
                  disabled
                  helperText="Account status"
                />
              </Box>
            </CardContent>
          </Card>

          {/* Invoice Details */}
          <Card style={{ marginTop: '10px' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <ReceiptIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Invoice Details
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Business Name"
                  value={tenant.invoice_name || ''}
                  onChange={(e) => handleInputChange('invoice_name', e.target.value)}
                  required
                />
                <TextField
                  fullWidth
                  label="Email"
                  value={tenant.invoice_email || ''}
                  onChange={(e) => handleInputChange('invoice_email', e.target.value)}
                  helperText="Email for invoice communications"
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={tenant.invoice_phone || ''}
                  onChange={(e) => handleInputChange('invoice_phone', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography variant="body2" color="text.secondary">
                          📞
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="Mobile"
                  value={tenant.invoice_mobile || ''}
                  onChange={(e) => handleInputChange('invoice_mobile', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography variant="body2" color="text.secondary">
                          📱
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <TextField
                  fullWidth
                  label="Invoice Address Line 1"
                  value={tenant.invoice_address_line1 || ''}
                  onChange={(e) => handleInputChange('invoice_address_line1', e.target.value)}
                  placeholder="Street address, building, etc."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography variant="body2" color="text.secondary">
                          📍
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="Invoice Address Line 2"
                  value={tenant.invoice_address_line2 || ''}
                  onChange={(e) => handleInputChange('invoice_address_line2', e.target.value)}
                  placeholder="Apartment, suite, unit, etc. (optional)"
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <TextField
                  fullWidth
                  label="Invoice City"
                  value={tenant.invoice_city || ''}
                  placeholder="City"
                  onChange={(e) => handleInputChange('invoice_city', e.target.value)}
                />
                <FormControl fullWidth>
                  <InputLabel>Invoice State/Province</InputLabel>
                  <Select
                    value={tenant.invoice_state || ''}
                    label="Invoice State/Province"
                    onChange={(e) => handleInputChange('invoice_state', e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Select State</em>
                    </MenuItem>
                    {states.map((state) => (
                      <MenuItem key={state.id} value={state.id}>
                        {state.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <TextField
                  fullWidth
                  label="Invoice Country"
                  value={tenant.invoice_country || ''}
                  onChange={(e) => handleInputChange('invoice_country', e.target.value)}
                  placeholder="Country"
                />
                <TextField
                  fullWidth
                  label="Invoice ZIP/Postal Code"
                  value={tenant.invoice_zip_code || ''}
                  onChange={(e) => handleInputChange('invoice_zip_code', e.target.value)}
                  placeholder="ZIP or Postal Code"
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <TextField
                  fullWidth
                  label="Website"
                  value={tenant.website || ''}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  placeholder="https://yourwebsite.com"
                  helperText="Optional website for invoices"
                />
              </Box>
            </CardContent>
          </Card>

          {/* GST Information */}
          <Card style={{ marginTop: '10px' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <ReceiptIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  GST Information (Optional)
                </Typography>
                <Chip label="Optional" size="small" color="info" variant="outlined" />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={tenant.company_name || ''}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  placeholder="Enter company name for GST"
                  helperText="Required if you have a GST number"
                />
                <TextField
                  fullWidth
                  label="GST Number"
                  value={tenant.gst_number || ''}
                  onChange={(e) => handleInputChange('gst_number', e.target.value)}
                  placeholder="Enter your GST number"
                  helperText="Enter your GST registration number"
                />
              </Box>

              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Note:</strong> GST information is optional and only required if your business is registered for GST.
                  This information will be used for invoice generation and tax compliance.
                </Typography>
              </Box>

            </CardContent>
          </Card>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveSettings}
            disabled={saving}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 'bold',
              marginTop: '10px',
            }}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Settings; 